import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { userSettingsKeys, useUserSettings } from "../../hooks/useUserSettings";
import type { UserSettings } from "../../utils/models";

type Waiting = { departmentId: string; target: UserSettings | undefined; run: () => void };

/**
 * Runs a callback once the user-settings cache entry as it is RIGHT NOW has reached the screen — after the
 * render that shows it, and after the printout popup's `setPrintoutSettings(defaultPrintoutSettings)`
 * effect has put it in the panel (call this hook after that effect, so its own effect runs later).
 *
 * Why not "the next time the settings change": a write's cache updates (optimistic, answer, rollback) reach
 * the screen in their own renders, on TanStack's notification schedule, so the next change may well be an
 * earlier write's — a flag armed for "the next change" is used up by the wrong one. Waiting for the entry
 * itself, by identity, can't be.
 *
 * If that entry is already on screen the callback runs at once. A newer request replaces a waiting one, and
 * a newer cache entry replaces the one waited for.
 *
 * Only for the department on screen: the popup stays mounted across a department switch, so a request made
 * from an earlier department's callback (an answer that arrived after the switch) is ignored, and a switch
 * drops a waiting one. Either would act on that department's settings in the new department's panel.
 */
export const useOnceUserSettingsShown = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const queryClient = useQueryClient();
	const { data: shown } = useUserSettings();
	const shownRef = useRef(shown);
	shownRef.current = shown;
	const waitingRef = useRef<Waiting | null>(null);
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo.id;
	const departmentRef = useRef(departmentId);
	departmentRef.current = departmentId;

	useEffect(() => {
		const waiting = waitingRef.current;
		if (!waiting) return;
		if (waiting.departmentId !== departmentId) {
			waitingRef.current = null;
			return;
		}
		// A newer write can replace the entry waited for before it is ever shown (structural sharing keeps a
		// same-data refetch's identity, so only a real change does). The newest entry then stands in for it:
		// it holds the awaited change or a later one, so waiting for the old object would wait forever.
		const latest = queryClient.getQueryData<UserSettings>(userSettingsKeys.department(departmentId, userId));
		if (shown !== waiting.target && shown !== latest) return;
		waitingRef.current = null;
		waiting.run();
	}, [shown, queryClient, departmentId, userId]);

	return useCallback(
		(run: () => void) => {
			if (departmentId !== departmentRef.current) return;
			const target = queryClient.getQueryData<UserSettings>(userSettingsKeys.department(departmentId, userId));
			if (shownRef.current === target) {
				waitingRef.current = null;
				run();
				return;
			}
			waitingRef.current = { departmentId, target, run };
		},
		[queryClient, departmentId, userId],
	);
};
